import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Card } from '../../styles/components';

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  border-radius: ${props => props.theme.borders.radius.md};
`;

const SkeletonCard = styled(Card)`
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const SkeletonLine = styled(SkeletonBase)<{ width?: string; height?: string }>`
  height: ${props => props.height || '1rem'};
  width: ${props => props.width || '100%'};
`;

const SkeletonCircle = styled(SkeletonBase)<{ size?: string }>`
  width: ${props => props.size || '3rem'};
  height: ${props => props.size || '3rem'};
  border-radius: 50%;
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.xl};
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const SkeletonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SkeletonStats = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.gray[200]};
`;

const SkeletonStatItem = styled.div`
  flex: 1;
  text-align: center;
  padding: ${props => props.theme.spacing.sm};
  background-color: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borders.radius.md};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

export function OrganizationCardSkeleton() {
  return (
    <SkeletonCard>
      <SkeletonHeader>
        <SkeletonCircle />
        <div style={{ flex: 1 }}>
          <SkeletonLine width="60%" height="1.5rem" />
          <SkeletonLine width="40%" height="0.875rem" style={{ marginTop: '0.5rem' }} />
        </div>
      </SkeletonHeader>
      
      <SkeletonLine width="80%" />
      <SkeletonLine width="70%" />
    </SkeletonCard>
  );
}

export function OrganizationCardsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <SkeletonGrid>
      {Array.from({ length: count }, (_, i) => (
        <OrganizationCardSkeleton key={i} />
      ))}
    </SkeletonGrid>
  );
}

const DetailsSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xl};
`;

const DetailsHeaderSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.md};
`;

const HeaderLeftSkeleton = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const DetailsGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.xl};
  
  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const InfoCardSkeleton = styled(Card)`
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const InfoGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.md};
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const InfoItemSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

export function OrganizationDetailsSkeleton() {
  return (
    <DetailsSkeleton>
      <DetailsHeaderSkeleton>
        <HeaderLeftSkeleton>
          <SkeletonLine width="4rem" height="2.5rem" />
          <SkeletonCircle size="4rem" />
          <div>
            <SkeletonLine width="15rem" height="2rem" />
            <SkeletonLine width="12rem" height="1rem" style={{ marginTop: '0.5rem' }} />
          </div>
        </HeaderLeftSkeleton>
        <SkeletonLine width="5rem" height="2.5rem" />
      </DetailsHeaderSkeleton>

      <DetailsGridSkeleton>
        <InfoCardSkeleton>
          <SkeletonLine width="10rem" height="1.5rem" />
          <InfoGridSkeleton>
            {Array.from({ length: 6 }, (_, i) => (
              <InfoItemSkeleton key={i}>
                <SkeletonLine width="60%" height="0.75rem" />
                <SkeletonLine width="80%" height="1rem" />
              </InfoItemSkeleton>
            ))}
          </InfoGridSkeleton>
        </InfoCardSkeleton>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {Array.from({ length: 3 }, (_, i) => (
            <Card key={i} style={{ padding: '1.5rem', textAlign: 'center' }}>
              <SkeletonCircle size="3rem" style={{ margin: '0 auto 1rem' }} />
              <SkeletonLine width="3rem" height="2rem" style={{ margin: '0 auto 0.5rem' }} />
              <SkeletonLine width="5rem" height="1rem" style={{ margin: '0 auto' }} />
            </Card>
          ))}
        </div>
      </DetailsGridSkeleton>
    </DetailsSkeleton>
  );
}